import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  LoaderCircle,
  Save,
} from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import ImageUploadGuidance from "../common/ImageUploadGuidance";
import InlineFormFileUpload, { IMAGE_TYPES } from "../file-upload/InlineFormFileUpload";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";
import SeoFields from "../seo/SeoFields";

const EMPTY_FORM = {
  title: "",
  content: "",
  published_date: "",
  date_precision: "exact",
  publication_year: "",
  status: "draft",
  featured: false,
  is_press_clipping: false,
  thumbnail_image: null,
  detail_image: null,
  meta_title: "",
  meta_description: "",
};

const NewsFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { newsId } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState("");
  const [detailPreviewUrl, setDetailPreviewUrl] = useState("");

  const isEditMode = mode === "edit";
  const pageTitle = useMemo(
    () => (isEditMode ? "Edit News" : "Create News"),
    [isEditMode],
  );

  useEffect(() => {
    if (!isEditMode) {
      setIsPageLoading(false);
      return;
    }

    const fetchNewsItem = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`/news/${newsId}`);
        const item = response.data?.data;
        if (!item) {
          showErrorToast("News item not found");
          navigate("/news");
          return;
        }

        setForm({
          title: item.title || item.name || "",
          content: item.content || "",
          published_date: item.published_date ? String(item.published_date).slice(0, 10) : "",
          date_precision: item.date_precision || "exact",
          publication_year: item.published_date
            ? String(item.published_date).slice(0, 4)
            : "",
          status: item.status || "draft",
          featured: Boolean(item.featured),
          is_press_clipping: Boolean(item.is_press_clipping),
          thumbnail_image: null,
          detail_image: null,
          meta_title: item.meta_title || "",
          meta_description: item.meta_description || "",
        });
        setThumbnailPreviewUrl(item.thumbnail_image_url || item.image_url || "");
        setDetailPreviewUrl(item.detail_image_url || "");
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load news item");
        navigate("/news");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchNewsItem();
  }, [isEditMode, navigate, newsId, showErrorToast]);

  const handleInputChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.title.trim()) nextErrors.title = "Title is required.";
    if (!form.content.trim()) nextErrors.content = "Content is required.";
    if (form.date_precision === "year") {
      const year = Number.parseInt(form.publication_year, 10);
      if (!Number.isInteger(year) || year < 1900 || year > 2100) {
        nextErrors.publication_year = "Enter a valid publication year.";
      }
    } else if (!form.published_date) {
      nextErrors.published_date = "Published date is required.";
    }
    if (!isEditMode && !form.thumbnail_image) {
      nextErrors.thumbnail_image = "Thumbnail image is required.";
    }
    if (!isEditMode && !form.detail_image) {
      nextErrors.detail_image = "Detail image is required.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const createFormData = () => {
    const payload = new FormData();
    payload.append("title", form.title.trim());
    payload.append("content", form.content.trim());
    payload.append(
      "published_date",
      form.date_precision === "year"
        ? `${form.publication_year}-01-01`
        : form.published_date,
    );
    payload.append("date_precision", form.date_precision);
    payload.append("status", form.status);
    payload.append("featured", String(form.featured));
    payload.append("is_press_clipping", String(form.is_press_clipping));
    payload.append("meta_title", form.meta_title.trim());
    payload.append("meta_description", form.meta_description.trim());

    if (form.thumbnail_image) {
      payload.append("thumbnail_image", form.thumbnail_image);
    }

    if (form.detail_image) {
      payload.append("detail_image", form.detail_image);
    }

    return payload;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const payload = createFormData();

      if (isEditMode) {
        await apiClient.put(`/news/${newsId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("News item updated successfully");
      } else {
        await apiClient.post("/news", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("News item created successfully");
      }

      navigate("/news");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save news item");
    } finally {
      setIsSaving(false);
    }
  };

  const renderImageUpload = ({
    label,
    field,
    previewUrl,
    helperText,
    requirementKey,
  }) => (
    <div className="space-y-2">
      <InlineFormFileUpload
        label={label}
        value={form[field]}
        onChange={(file) => handleInputChange(field, file)}
        existingFiles={previewUrl ? [previewUrl] : []}
        accept={IMAGE_TYPES}
        maxSizeBytes={10 * 1024 * 1024}
        required={!isEditMode}
        error={formErrors[field]}
        helperText={`${helperText} Maximum 10 MB.`}
      />
      <ImageUploadGuidance
        requirementKey={requirementKey}
        file={form[field]}
        src={previewUrl}
      />
    </div>
  );

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading news editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6">
      <div>
        <Button
          type="button"
          variant="ghost"
          className="-ml-3 mb-2 rounded-lg px-3 text-slate-500"
          onClick={() => navigate("/news")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to News
        </Button>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">
          {pageTitle}
        </h1>
      </div>

      <form
        onSubmit={handleSave}
        className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl"
      >
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Title</label>
              <Input
                value={form.title}
                onChange={(event) => handleInputChange("title", event.target.value)}
                className={`rounded-lg ${
                  formErrors.title ? "border-red-300 focus-visible:ring-red-500" : ""
                }`}
              />
              <FieldError>{formErrors.title}</FieldError>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Date Display
                </label>
                <Select
                  value={form.date_precision}
                  onValueChange={(value) =>
                    handleInputChange("date_precision", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select date display" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exact">Exact date</SelectItem>
                    <SelectItem value="year">Year only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  {form.date_precision === "year"
                    ? "Publication Year"
                    : "Published Date"}
                </label>
                <Input
                  type={form.date_precision === "year" ? "number" : "date"}
                  min={form.date_precision === "year" ? "1900" : undefined}
                  max={form.date_precision === "year" ? "2100" : undefined}
                  value={
                    form.date_precision === "year"
                      ? form.publication_year
                      : form.published_date
                  }
                  onChange={(event) =>
                    handleInputChange(
                      form.date_precision === "year"
                        ? "publication_year"
                        : "published_date",
                      event.target.value,
                    )
                  }
                  className={`rounded-lg ${
                    formErrors.published_date || formErrors.publication_year
                      ? "border-red-300 focus-visible:ring-red-500"
                      : ""
                  }`}
                />
                <FieldError>
                  {formErrors.published_date || formErrors.publication_year}
                </FieldError>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Status</label>
                <Select
                  value={form.status}
                  onValueChange={(value) => handleInputChange("status", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
              <input
                id="featured"
                type="checkbox"
                checked={form.featured}
                onChange={(event) =>
                  handleInputChange("featured", event.target.checked)
                }
                className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
              />
              <label htmlFor="featured" className="text-sm font-medium text-slate-700">
                Mark as featured news
              </label>
            </div>

            <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
              <input
                id="is_press_clipping"
                type="checkbox"
                checked={form.is_press_clipping}
                onChange={(event) =>
                  handleInputChange("is_press_clipping", event.target.checked)
                }
                className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
              />
              <label
                htmlFor="is_press_clipping"
                className="text-sm font-semibold text-slate-700"
              >
                Use press clipping layout (show the complete image without cropping)
              </label>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Content</label>
              <Textarea
                value={form.content}
                onChange={(event) => handleInputChange("content", event.target.value)}
                rows={12}
                className={`rounded-lg ${
                  formErrors.content ? "border-red-300 focus-visible:ring-red-500" : ""
                }`}
                placeholder="Write the full news content here..."
              />
              <FieldError>{formErrors.content}</FieldError>
            </div>
          </div>

          <div className="space-y-4">
            {renderImageUpload({
              label: "Thumbnail Image",
              field: "thumbnail_image",
              previewUrl: thumbnailPreviewUrl,
              helperText: "Used on the listing card. JPG, PNG, GIF, or WEBP.",
              requirementKey: "newsThumbnail",
            })}

            {renderImageUpload({
              label: "Detail Image",
              field: "detail_image",
              previewUrl: detailPreviewUrl,
              helperText: "Used on the news detail page. JPG, PNG, GIF, or WEBP.",
              requirementKey: "newsDetail",
            })}
          </div>
        </div>

        <SeoFields
          metaTitle={form.meta_title}
          metaDescription={form.meta_description}
          onChange={handleInputChange}
          className="mt-6"
        />

        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="rounded-lg"
            onClick={() => navigate("/news")}
          >
            Cancel
          </Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isSaving ? "Saving..." : isEditMode ? "Update News" : "Create News"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewsFormPage;
